import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/components/TextComponent';
import type { TextProps } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { common, designSystem } from '@/styles/styles';

import { ds } from '../lib/design.system';

/** Statuses shown as a chip on the entry card. Open/live/voided entries show no chip. */
export const EntryStatus = {
    Won: 'won',
    Lost: 'lost',
    Refunded: 'refunded',
    CashedOut: 'cashedOut',
    Sold: 'sold', // 'Predictions' cashed-out equivalent — a sold position
    Unconfirmed: 'unconfirmed',
    Open: 'open',
} as const;

export type EntryStatus = (typeof EntryStatus)[keyof typeof EntryStatus];

type StatusStyle = { bgColor: string; textColor: TextProps['color'] };

const STATUS_STYLES: Record<EntryStatus, StatusStyle> = {
    [EntryStatus.Open]: { bgColor: designSystem.colors.neutral600, textColor: ds.colorToken.text.primary },
    [EntryStatus.Won]: { bgColor: ds.colorValue.background.success.strong, textColor: ds.colorToken.text.primary },
    [EntryStatus.Lost]: { bgColor: ds.colorValue.background.danger.strong, textColor: ds.colorToken.text.primary },
    [EntryStatus.Refunded]: { bgColor: designSystem.colors.neutral400, textColor: ds.colorToken.text.primary },
    [EntryStatus.CashedOut]: {
        bgColor: ds.colorValue.background.accent.blue.strong,
        textColor: ds.colorToken.text.primary,
    },
    [EntryStatus.Sold]: { bgColor: ds.colorValue.background.accent.blue.strong, textColor: ds.colorToken.text.primary },
    [EntryStatus.Unconfirmed]: { bgColor: designSystem.colors.neutral400, textColor: ds.colorToken.text.primary },
};

export type EntryStatusBadgeProps = {
    status: EntryStatus;
    showOpen?: boolean;
};

export const EntryStatusBadge = ({ status, showOpen = false }: EntryStatusBadgeProps) => {
    const { t } = useTranslation('bets');

    if (status === EntryStatus.Open && !showOpen) {
        return null;
    }

    const { bgColor, textColor } = STATUS_STYLES[status];
    return (
        <Box
            borderRadius="r7"
            paddingVertical="s4"
            paddingHorizontal="s6"
            alignItems="center"
            style={[{ backgroundColor: bgColor }, common.borderCurve]}
            testID={'entry-status-badge-' + status}
        >
            <Text color={textColor} variant="labelMedium" fontWeight={600}>
                {t(`entry_status_badge.${status}`)}
            </Text>
        </Box>
    );
};
